import type { FC, FormEvent, ReactNode } from 'react'

import { useId, useState } from 'react'
import { ArrowRight, Check, ChevronDown } from 'lucide-react'
import { isEmail } from '@/lib/format'
import { cn } from '@/lib/utils'
import Button from '@/components/generals/button'
import { contactTopics } from '@/constants/content'

type Status = 'idle' | 'sending' | 'done' | 'error'

const EMPTY = { name: '', email: '', topic: contactTopics[0], message: '', company: '' }

const inputClass = 'h-12 w-full border-b border-line bg-transparent text-base text-bone outline-none transition-colors duration-300 placeholder:text-bone/35 focus:border-bone'

/** "Write to the studio": messages are saved for the team to answer by email. */
const ContactForm: FC = (): ReactNode => {
    const id = useId()
    const [form, setForm] = useState(EMPTY)
    const [errors, setErrors] = useState<Record<string, string>>({})
    const [status, setStatus] = useState<Status>('idle')
    const [message, setMessage] = useState('')

    const set = (field: keyof typeof EMPTY, value: string): void => {
        setForm(current => ({ ...current, [field]: value }))

        if (errors[field]) setErrors(current => ({ ...current, [field]: '' }))
    }

    const submit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
        event.preventDefault()

        const nextErrors: Record<string, string> = {}

        if (!form.name.trim()) nextErrors.name = 'Please tell us your name.'
        if (!isEmail(form.email)) nextErrors.email = 'Please enter a valid email address.'
        if (form.message.trim().length < 10) nextErrors.message = 'Please write at least a sentence.'

        setErrors(nextErrors)

        if (Object.keys(nextErrors).length) return

        setStatus('sending')
        setMessage('')

        try {
            const response = await fetch('/api/contact', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(form)
            })

            const payload = await response.json().catch(() => null) as { message?: string, errors?: Record<string, string> } | null

            if (!response.ok) {
                if (payload?.errors) setErrors(payload.errors)

                throw new Error(payload?.message || 'Something went wrong.')
            }

            setStatus('done')
            setForm(EMPTY)
        } catch (error) {
            setStatus('error')
            setMessage(error instanceof Error ? error.message : 'Something went wrong. Please try again.')
        }
    }

    if (status === 'done') {
        return (
            <div role='status' className='flex min-h-[420px] flex-col items-start justify-center border border-line p-8 md:p-10'>
                <span className='flex size-12 items-center justify-center rounded-full bg-acid text-ink'>
                    <Check aria-hidden='true' className='size-5' />
                </span>

                <p className='mt-8 display-m uppercase'>
                    Message received
                </p>

                <p className='mt-4 max-w-[40ch] body-copy text-bone/70'>
                    Thank you for writing. Someone from the studio will reply by email within two working days.
                </p>

                <button
                    type='button'
                    onClick={() => setStatus('idle')}
                    className='mt-8 label-mono text-acid underline underline-offset-4'
                >
                    Write another message
                </button>
            </div>
        )
    }

    return (
        <form onSubmit={submit} noValidate className='space-y-8'>
            <div className='grid gap-8 sm:grid-cols-2 sm:gap-6'>
                <div>
                    <label htmlFor={`${id}-name`} className='meta-mono text-ash'>
                        Name
                    </label>

                    <input
                        id={`${id}-name`}
                        name='name'
                        autoComplete='name'
                        value={form.name}
                        onChange={event => set('name', event.target.value)}
                        placeholder='Your name'
                        aria-invalid={Boolean(errors.name)}
                        aria-describedby={errors.name ? `${id}-name-error` : undefined}
                        className={cn(inputClass, errors.name && 'border-destructive')}
                    />

                    {errors.name ? <p id={`${id}-name-error`} className='mt-2 text-sm text-destructive'>{errors.name}</p> : null}
                </div>

                <div>
                    <label htmlFor={`${id}-email`} className='meta-mono text-ash'>
                        Email
                    </label>

                    <input
                        id={`${id}-email`}
                        name='email'
                        type='email'
                        autoComplete='email'
                        value={form.email}
                        onChange={event => set('email', event.target.value)}
                        placeholder='you@domain.com'
                        aria-invalid={Boolean(errors.email)}
                        aria-describedby={errors.email ? `${id}-email-error` : undefined}
                        className={cn(inputClass, errors.email && 'border-destructive')}
                    />

                    {errors.email ? <p id={`${id}-email-error`} className='mt-2 text-sm text-destructive'>{errors.email}</p> : null}
                </div>
            </div>

            <div>
                <label htmlFor={`${id}-topic`} className='meta-mono text-ash'>
                    Topic
                </label>

                <div className='relative'>
                    <select
                        id={`${id}-topic`}
                        name='topic'
                        value={form.topic}
                        onChange={event => set('topic', event.target.value)}
                        className={cn(inputClass, 'appearance-none pr-8')}
                    >
                        {contactTopics.map(topic => (
                            <option key={topic} value={topic} className='bg-coal text-bone'>
                                {topic}
                            </option>
                        ))}
                    </select>

                    <ChevronDown aria-hidden='true' className='pointer-events-none absolute right-0 top-1/2 size-4 -translate-y-1/2 text-ash' />
                </div>
            </div>

            <div>
                <label htmlFor={`${id}-message`} className='meta-mono text-ash'>
                    Message
                </label>

                <textarea
                    id={`${id}-message`}
                    name='message'
                    rows={5}
                    value={form.message}
                    onChange={event => set('message', event.target.value)}
                    placeholder='Tell us everything.'
                    aria-invalid={Boolean(errors.message)}
                    aria-describedby={errors.message ? `${id}-message-error` : undefined}
                    className={cn('w-full resize-none border-b border-line bg-transparent py-3 text-base text-bone outline-none transition-colors duration-300 placeholder:text-bone/35 focus:border-bone', errors.message && 'border-destructive')}
                />

                {errors.message ? <p id={`${id}-message-error`} className='mt-2 text-sm text-destructive'>{errors.message}</p> : null}
            </div>

            <input
                type='text'
                name='company'
                tabIndex={-1}
                autoComplete='off'
                aria-hidden='true'
                value={form.company}
                onChange={event => set('company', event.target.value)}
                className='hidden'
            />

            {message ? <p role='alert' className='text-sm text-destructive'>{message}</p> : null}

            <Button
                type='submit'
                className='w-full'
                disabled={status === 'sending'}
                icon={<ArrowRight className='size-4 transition-transform duration-500 group-hover/button:translate-x-1' />}
            >
                {status === 'sending' ? 'Sending' : 'Send message'}
            </Button>
        </form>
    )
}

export default ContactForm
